/**
 * Общие типы и хелперы для под-view вопроса (Фаза 2, диспетчер QuestionView).
 *
 * Под-view не знают про WebSocket — они получают «текущий вопрос», свой
 * вариант отображения (answer/spectate), уже данный ответ (myAnswer),
 * раскрытие (reveal), живую статистику (counts) и колбэк onAnswer, в который
 * отдают унифицированный `PlayerAnswer` (протокол §8). Экран игрока сам
 * превратит его в wire-`AnswerPayload` через `makeAnswerPayload`.
 */
import type {
  PlayerAnswer,
  PlayerPos,
  QuestionPayload,
  RevealPayload,
  RunDeadPayload,
  WordCountEntry,
} from '../../protocol';

/** Единый набор пропсов для всех под-view (Choice/Poll/Open/Match/Order/Word). */
export interface QuestionBodyProps {
  question: QuestionPayload;
  /** answer — экран игрока (интерактив); spectate — экран ведущего (только просмотр + статистика). */
  variant: 'answer' | 'spectate';
  /** Уже данный игроком ответ (для блокировки/восстановления после отправки). */
  myAnswer: PlayerAnswer | null;
  /** Раскрытие правильного ответа (событие reveal); null, пока не пришло. */
  reveal: RevealPayload | null;
  /** Живая статистика counts[i] (только choice/poll); null для open/match/order. */
  counts: number[] | null;
  /**
   * Живое облако word-вопроса из события stats (И8 Фазы 10): заполняет HostSession
   * для spectate-предпросмотра ведущего; null для прочих типов (обратная
   * совместимость пропсов). Финальное облако приходит в reveal.words.
   */
  liveWords?: WordCountEntry[] | null;
  /** Число участников комнаты — передаёт ведущий для сводки «N из M» при отсутствии counts. */
  participants?: number;
  /** Колбэк отправки ответа; под-view вызывает его с type-специфичным PlayerAnswer. */
  onAnswer: (answer: PlayerAnswer) => void;
  /**
   * Run-вопрос (Фаза 14, эпик B): отправка позиции игрока — RunView шлёт
   * нормализованные координаты 0..1 с частотой ~3 Гц и только при изменении
   * (сервер зачитывает по снапшоту при close, §3.3). PlayerGame превращает в
   * WS-событие pos. Опционален — прочие view его игнорируют.
   */
  onPos?: (x: number, y: number) => void;
  /**
   * Run-вопрос: последний пакет positions (событие ~5 Гц, только ведущему;
   * §3.3) — RunHostView рисует доты всех игроков и считает счётчики углов.
   * null/undefined — пакетов ещё не было (или тип не run).
   */
  positions?: PlayerPos[] | null;
  /**
   * Run-вопрос (0.14.3, S1/R2): серверное Unix-мс сборки последнего пакета
   * positions (PositionsPayload.t) — база лерп-интерполяции дотов ведущего
   * (playback delay) и EMA-уточнение серверного offset. null — пакетов не было
   * (или старый сервер без t; t есть всегда — протокол 0.14.x).
   */
  positionsT?: number | null;
  /**
   * Run-вопрос (0.14.3, S4): персональный dead-пуш «run_dead» — сервер
   * зафиксировал гибель игрока; RunView мгновенно взрывает и переводит в
   * наблюдатели (идемпотентно локальному детекту — кто первый). null — жив.
   */
  runDead?: RunDeadPayload | null;
  /**
   * Ник игрока (run): подпись над человечком на канве игрока (§3.4).
   * Опционален — превью/тесты могут не задавать.
   */
  playerNick?: string;
}

/**
 * Нормализация открытого текста для UX-сверки с accept (клиентская подсветка
 * «верно/неверно» после reveal). Серверная нормализация — источник истины для
 * скоринга; здесь — лишь предварительная оценка для UI.
 */
export function normalizeOpenText(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, ' ');
}
